'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  useHealthLog, 
  useCreateHealthLog, 
  useUpdateHealthLog 
} from '@/features/health/hooks/useHealthLogs';
import { useHealthLogTypes } from '@/features/health/hooks/useHealthLogTypes';
import { useChildren } from '@/features/children/hooks/useChildren';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { FileUpload } from '@/components/ui/FileUpload';
import { PageHeader } from '@/components/layout/PageHeader';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Alert } from '@/components/ui/Alert';
import { SeveritySlider } from '@/features/health/components/SeveritySlider';
import { HealthLogTypeSelector } from '@/features/health/components/HealthLogTypeSelector';
import { DynamicHealthFields } from '@/features/health/components/DynamicHealthFields';
import { useToast } from '@/core/hooks/useToast';
import type { HealthLogType, HealthLog } from '@/features/health/types/healthTypes';

// Base validation schema
const baseSchema = z.object({
  childId: z.string().min(1, 'Please select a child'),
  logTypeId: z.string().min(1, 'Please select a log type'),
  occurredAt: z.date({ required_error: 'Please enter when this occurred' }),
  durationMinutes: z.number().min(0).optional().nullable(),
  severity: z.number().min(1).max(10).optional().nullable(),
  notes: z.string().max(5000).optional(),
  location: z.string().max(200).optional(),
  data: z.record(z.any()).optional(),
});

type FormData = z.infer<typeof baseSchema>;

export function HealthLogFormPage(): React.ReactElement {
  const navigate = useNavigate();
  const { familyId, logId } = useParams<{ familyId: string; logId?: string }>();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const isEditing = !!logId;
  const preselectedChildId = searchParams.get('childId') || '';
  const preselectedTypeId = searchParams.get('type') || '';

  // State for file uploads
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);

  // Fetch data
  const { data: children, isLoading: childrenLoading } = useChildren(familyId!);
  const { data: logTypes, isLoading: typesLoading } = useHealthLogTypes();
  const { data: existingLog, isLoading: logLoading } = useHealthLog(logId || '', {
    enabled: isEditing,
  });

  const createHealthLog = useCreateHealthLog();
  const updateHealthLog = useUpdateHealthLog();

  // Form setup
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      childId: preselectedChildId,
      logTypeId: preselectedTypeId,
      occurredAt: new Date(),
      durationMinutes: null,
      severity: null,
      notes: '',
      location: '',
      data: {},
    },
  });

  // Watch for type changes to show dynamic fields
  const selectedTypeId = watch('logTypeId');
  const selectedType = useMemo(() => {
    return logTypes?.find((t: HealthLogType) => t.id === selectedTypeId);
  }, [logTypes, selectedTypeId]);

  // Populate form when editing
  useEffect(() => {
    if (existingLog && isEditing) {
      reset({
        childId: existingLog.childId,
        logTypeId: existingLog.logTypeId,
        occurredAt: new Date(existingLog.occurredAt),
        durationMinutes: existingLog.durationMinutes || null,
        severity: existingLog.severity || null,
        notes: existingLog.notes || '',
        location: existingLog.location || '',
        data: existingLog.data || {},
      });
      if (existingLog.attachments) {
        setExistingAttachments(existingLog.attachments);
      }
    }
  }, [existingLog, isEditing, reset]);

  // Auto-select first child if only one
  useEffect(() => {
    if (children && children.length === 1 && !preselectedChildId) {
      setValue('childId', children[0].id);
    }
  }, [children, preselectedChildId, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        ...data,
        occurredAt: data.occurredAt.toISOString(),
        durationMinutes: data.durationMinutes || undefined,
        severity: data.severity || undefined,
      };

      if (isEditing) {
        await updateHealthLog.mutateAsync({
          id: logId!,
          ...payload,
        });
        showToast({
          type: 'success',
          title: 'Log updated',
          message: 'Health log has been updated successfully.',
        });
      } else {
        await createHealthLog.mutateAsync(payload);
        showToast({
          type: 'success',
          title: 'Log created',
          message: 'Health log has been created successfully.',
        });
      }

      navigate(`/family/${familyId}/health/logs?childId=${data.childId}`);
    } catch (error: any) {
      showToast({
        type: 'error',
        title: isEditing ? 'Update failed' : 'Creation failed',
        message: error.message || 'An error occurred. Please try again.',
      });
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }
    navigate(-1);
  };

  const handleFileAdd = (files: File[]) => {
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleFileRemove = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExistingFileRemove = (index: number) => {
    setExistingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const isLoading = childrenLoading || typesLoading || (isEditing && logLoading);
  const isSaving = createHealthLog.isPending || updateHealthLog.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Back Button */}
      <button
        onClick={handleCancel}
        className="flex items-center text-warm-gray hover:text-navy mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Cancel
      </button>

      {/* Page Header */}
      <PageHeader
        title={isEditing ? 'Edit Health Log' : 'New Health Log'}
        description={isEditing ? 'Update the health log entry' : 'Record a new health event'}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Child Selection */}
        <Card className="p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">
            Child
          </h2>
          
          <Controller
            name="childId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Select Child"
                error={errors.childId?.message}
                disabled={isEditing}
              >
                <option value="">Select a child...</option>
                {children?.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.first_name} {child.last_name}
                    {child.nickname && ` (${child.nickname})`}
                  </option>
                ))}
              </Select>
            )}
          />
        </Card>

        {/* Log Type Selection */}
        <Card className="p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">
            Log Type
          </h2>
          
          <Controller
            name="logTypeId"
            control={control}
            render={({ field }) => (
              <HealthLogTypeSelector
                types={logTypes || []}
                selectedTypeId={field.value}
                onChange={field.onChange}
                error={errors.logTypeId?.message}
                disabled={isEditing}
              />
            )}
          />
        </Card>

        {/* Date/Time and Location */}
        <Card className="p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">
            When & Where
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="occurredAt"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label="Date & Time"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.occurredAt?.message}
                  maxDate={new Date()}
                />
              )}
            />

            <Input
              label="Location (Optional)"
              placeholder="e.g., Home, School, Hospital"
              {...register('location')}
              error={errors.location?.message}
            />
          </div>
        </Card>

        {/* Severity (if applicable) */}
        {selectedType?.fieldsSchema?.severity !== undefined && (
          <Card className="p-6">
            <h2 className="text-lg font-display font-semibold text-navy mb-4">
              Severity
            </h2>
            
            <Controller
              name="severity"
              control={control}
              render={({ field }) => (
                <SeveritySlider
                  value={field.value || 1}
                  onChange={field.onChange}
                  showLabels
                />
              )}
            />
          </Card>
        )}

        {/* Duration (if applicable) */}
        {selectedType?.fieldsSchema?.duration_seconds !== undefined && (
          <Card className="p-6">
            <h2 className="text-lg font-display font-semibold text-navy mb-4">
              Duration
            </h2>
            
            <div className="flex items-center gap-4">
              <Input
                type="number"
                label="Duration"
                placeholder="0"
                min={0}
                {...register('durationMinutes', { valueAsNumber: true })}
                error={errors.durationMinutes?.message}
                className="w-32"
              />
              <span className="text-warm-gray mt-6">minutes</span>
            </div>
          </Card>
        )}

        {/* Dynamic Fields based on Log Type */}
        {selectedType && (
          <Card className="p-6">
            <h2 className="text-lg font-display font-semibold text-navy mb-4">
              {selectedType.name} Details
            </h2>
            
            <Controller
              name="data"
              control={control}
              render={({ field }) => (
                <DynamicHealthFields
                  schema={selectedType.fieldsSchema}
                  values={field.value || {}}
                  onChange={field.onChange}
                />
              )}
            />
          </Card>
        )}

        {/* Notes */}
        <Card className="p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">
            Notes
          </h2>
          
          <Textarea
            label="Additional Notes (Optional)"
            placeholder="Add any additional observations, context, or details..."
            rows={4}
            {...register('notes')}
            error={errors.notes?.message}
          />
        </Card>

        {/* Attachments */}
        <Card className="p-6">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">
            Attachments
          </h2>
          
          {existingAttachments.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-warm-gray mb-2">Existing files:</p>
              <div className="space-y-2">
                {existingAttachments.map((file, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-cream rounded-lg"
                  >
                    <span className="text-sm text-navy truncate">{file.filename}</span>
                    <button
                      type="button"
                      onClick={() => handleExistingFileRemove(index)}
                      className="text-red-500 hover:text-red-600 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <FileUpload
            onFilesAdd={handleFileAdd}
            files={attachments}
            onFileRemove={handleFileRemove}
            accept="image/*,.pdf,.doc,.docx"
            maxFiles={5}
            maxSizeMB={10}
          />
        </Card>

        {/* Error Summary */}
        {Object.keys(errors).length > 0 && (
          <Alert type="error" className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Please fix the following errors:</p>
              <ul className="mt-1 text-sm list-disc list-inside">
                {Object.entries(errors).map(([key, error]) => (
                  <li key={key}>{error?.message}</li>
                ))}
              </ul>
            </div>
          </Alert>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update Log' : 'Create Log'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default HealthLogFormPage;


